import React, { useState, useEffect } from 'react';
import { 
  ChevronRight, Loader2, CheckCircle, AlertCircle, User, Phone, Mail, 
  Calendar, CreditCard, MapPin, FileText, Upload, IndianRupee, Armchair, Camera, X
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  { id: 'starter', name: '🌱 Focus Starter (1 Month)', price: 1500 },
  { id: 'saver', name: '💪 Focus Saver (3 Months)', price: 4200 },
  { id: 'pro', name: '⭐ Focus Pro (6 Months)', price: 7800 },
  { id: 'elite', name: '🏆 Focus Elite (12 Months)', price: 14400 },
  { id: 'other', name: 'Other', price: 0 }
];

const EXAMS = ['UPSC', 'CA', 'NEET', 'JEE', 'SSC', 'Banking', 'GATE', 'Other'];
const PURPOSES = ['📚 Study', '💻 Work', '📝 Practice', 'Other'];
const ID_TYPES = ['Aadhaar', 'PAN', 'Driving Licence', 'Voter ID', 'Other'];

const FileUpload = ({ label, accept, value, onChange, preview }) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show preview
    if (file.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(file));
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/api/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      onChange(res.data.filename);
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    onChange(null);
    setPreviewUrl(null);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      
      {value ? (
        <div className="relative">
          {previewUrl || (value && value.match(/\.(jpg|jpeg|png|gif|webp)$/i)) ? (
            <div className="relative w-full h-32 bg-gray-100 rounded-xl overflow-hidden">
              <img 
                src={previewUrl || `${API}/api/uploads/${value}`} 
                alt="Preview" 
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={clearFile}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-green-700 text-sm">File uploaded: {value}</span>
              <button
                type="button"
                onClick={clearFile}
                className="ml-auto p-1 text-red-500 hover:bg-red-50 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <label className="block cursor-pointer">
          <div className={`border-2 border-dashed rounded-xl p-6 text-center transition ${
            uploading ? 'border-purple-400 bg-purple-50' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
          }`}>
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                <span className="text-purple-600 font-medium">Uploading...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Camera className="w-6 h-6 text-purple-600" />
                </div>
                <span className="text-gray-600 font-medium">Tap to upload</span>
                <span className="text-xs text-gray-400">JPG, PNG up to 5MB</span>
              </div>
            )}
          </div>
          <input
            type="file"
            accept={accept}
            onChange={handleFileChange}
            className="hidden"
            disabled={uploading}
          />
        </label>
      )}
    </div>
  );
};

const RegistrationForm = ({ onSuccess, onBack }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableSeats, setAvailableSeats] = useState([]);
  const [upiInfo, setUpiInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    join_date: new Date().toISOString().split('T')[0],
    plan: '',
    other_plan: '',
    seat: '',
    exam: '',
    purpose: '',
    other_purpose: '',
    address: '',
    id_type: '',
    other_id: '',
    id_number: '',
    photo_file: null,
    id_proof_file: null,
    payment_type: '',
    payment_screenshot_file: null
  });

  useEffect(() => {
    fetchAvailableSeats();
    fetchUPIInfo();
  }, []);

  const fetchAvailableSeats = async () => {
    try {
      const res = await axios.get(`${API}/api/seats/available`);
      setAvailableSeats(res.data.available_seats);
    } catch (err) {
      console.error('Failed to fetch seats:', err);
    }
  };

  const fetchUPIInfo = async () => {
    try {
      const res = await axios.get(`${API}/api/payments/upi-info`);
      setUpiInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch UPI info:', err);
    }
  };

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.name || !formData.phone) {
          setError('Please fill in all required fields');
          return false;
        }
        if (formData.phone.length < 10) {
          setError('Please enter a valid phone number');
          return false;
        }
        break;
      case 2:
        if (!formData.plan || !formData.seat) {
          setError('Please select a plan and seat');
          return false;
        }
        break;
      case 3:
        if (!formData.exam || !formData.purpose) {
          setError('Please fill in study details');
          return false;
        }
        break;
      case 4:
        if (!formData.id_type || !formData.id_number) {
          setError('Please provide ID details');
          return false;
        }
        break;
      case 5:
        if (!formData.payment_type) {
          setError('Please select payment type');
          return false;
        }
        break;
      default:
        break;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
    setError('');
  };

  const submitForm = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/api/students`, formData);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = PLANS.find(p => p.name === formData.plan);

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-purple-800 flex items-center gap-2">
              <User className="w-6 h-6" /> Personal Details
            </h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateForm('name', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">+91</span>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateForm('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className="w-full pl-14 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
                  placeholder="10-digit number"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email (Optional)</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateForm('email', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Join Date *</label>
              <input
                type="date"
                value={formData.join_date}
                onChange={(e) => updateForm('join_date', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-purple-800 flex items-center gap-2">
              <CreditCard className="w-6 h-6" /> Select Plan & Seat
            </h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Choose Plan *</label>
              <div className="space-y-2">
                {PLANS.map(plan => (
                  <label
                    key={plan.id}
                    className={`block p-4 border-2 rounded-xl cursor-pointer transition ${
                      formData.plan === plan.name 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="plan"
                      value={plan.name}
                      checked={formData.plan === plan.name}
                      onChange={(e) => updateForm('plan', e.target.value)}
                      className="sr-only"
                    />
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{plan.name}</span>
                      {plan.price > 0 && (
                        <span className="text-purple-600 font-bold">₹{plan.price.toLocaleString()}</span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
              {formData.plan === 'Other' && (
                <input
                  type="text"
                  value={formData.other_plan}
                  onChange={(e) => updateForm('other_plan', e.target.value)}
                  className="w-full mt-2 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
                  placeholder="Specify your plan"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Armchair className="w-4 h-4 inline mr-1" /> Select Seat * ({availableSeats.length} available)
              </label>
              <div className="grid grid-cols-6 gap-2">
                {Array.from({ length: 36 }, (_, i) => i + 1).map(num => {
                  const isAvailable = availableSeats.includes(num);
                  const isSelected = formData.seat === num;
                  return (
                    <button
                      key={num}
                      type="button"
                      onClick={() => isAvailable && updateForm('seat', num)}
                      disabled={!isAvailable}
                      className={`p-2 rounded-lg text-sm font-medium transition ${
                        isSelected
                          ? 'bg-purple-600 text-white'
                          : isAvailable
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-400 cursor-not-allowed'
                      }`}
                    >
                      {num}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                🟢 Available | 🔴 Occupied | 🟣 Selected
              </p>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-purple-800 flex items-center gap-2">
              <FileText className="w-6 h-6" /> Study Details
            </h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Exam Preparing For *</label>
              <select
                value={formData.exam}
                onChange={(e) => updateForm('exam', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
              >
                <option value="">Select exam</option>
                {EXAMS.map(exam => (
                  <option key={exam} value={exam}>{exam}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Purpose of Joining *</label>
              <div className="grid grid-cols-2 gap-2">
                {PURPOSES.map(purpose => (
                  <label
                    key={purpose}
                    className={`p-3 border-2 rounded-xl cursor-pointer text-center transition ${
                      formData.purpose === purpose
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="purpose"
                      value={purpose}
                      checked={formData.purpose === purpose}
                      onChange={(e) => updateForm('purpose', e.target.value)}
                      className="sr-only"
                    />
                    {purpose}
                  </label>
                ))}
              </div>
              {formData.purpose === 'Other' && (
                <input
                  type="text"
                  value={formData.other_purpose}
                  onChange={(e) => updateForm('other_purpose', e.target.value)}
                  className="w-full mt-2 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
                  placeholder="Specify purpose"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin className="w-4 h-4 inline mr-1" /> Address (Optional)
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => updateForm('address', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
                rows={3}
                placeholder="Enter your address"
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-purple-800 flex items-center gap-2">
              <FileText className="w-6 h-6" /> KYC Details
            </h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">ID Proof Type *</label>
              <select
                value={formData.id_type}
                onChange={(e) => updateForm('id_type', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
              >
                <option value="">Select ID type</option>
                {ID_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {formData.id_type === 'Other' && (
                <input
                  type="text"
                  value={formData.other_id}
                  onChange={(e) => updateForm('other_id', e.target.value)}
                  className="w-full mt-2 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
                  placeholder="Specify ID type"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ID Number *</label>
              <input
                type="text"
                value={formData.id_number}
                onChange={(e) => updateForm('id_number', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none"
                placeholder="Enter ID number"
              />
            </div>

            <FileUpload
              label="📸 Your Photo"
              accept="image/*"
              value={formData.photo_file}
              onChange={(filename) => updateForm('photo_file', filename)}
            />

            <FileUpload
              label="📄 ID Proof (Aadhaar/PAN/DL)"
              accept="image/*,.pdf"
              value={formData.id_proof_file}
              onChange={(filename) => updateForm('id_proof_file', filename)}
            />
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-purple-800 flex items-center gap-2">
              <IndianRupee className="w-6 h-6" /> Payment
            </h2>

            {selectedPlan && selectedPlan.price > 0 && (
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-4 rounded-xl">
                <p className="text-sm text-gray-600">Amount to Pay</p>
                <p className="text-3xl font-bold text-purple-800">₹{selectedPlan.price.toLocaleString()}</p>
                <p className="text-sm text-gray-500">{formData.plan}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Type *</label>
              <div className="grid grid-cols-2 gap-3">
                <label
                  className={`p-4 border-2 rounded-xl cursor-pointer text-center transition ${
                    formData.payment_type === 'Cash'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment_type"
                    value="Cash"
                    checked={formData.payment_type === 'Cash'}
                    onChange={(e) => updateForm('payment_type', e.target.value)}
                    className="sr-only"
                  />
                  <span className="text-2xl">💵</span>
                  <p className="font-medium mt-1">Cash</p>
                </label>
                <label
                  className={`p-4 border-2 rounded-xl cursor-pointer text-center transition ${
                    formData.payment_type === 'Online'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment_type"
                    value="Online"
                    checked={formData.payment_type === 'Online'}
                    onChange={(e) => updateForm('payment_type', e.target.value)}
                    className="sr-only"
                  />
                  <span className="text-2xl">📲</span>
                  <p className="font-medium mt-1">UPI/Online</p>
                </label>
              </div>
            </div>

            {formData.payment_type === 'Online' && upiInfo && (
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-xl space-y-3">
                <h3 className="font-bold text-gray-800">Pay via UPI</h3>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-sm text-gray-500">UPI ID</p>
                  <p className="text-lg font-mono font-bold text-blue-600">{upiInfo.upi_id}</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-sm text-gray-500">Phone Number</p>
                  <p className="text-lg font-bold">{upiInfo.phone}</p>
                </div>
                <p className="text-sm text-gray-600">
                  After payment, upload screenshot below:
                </p>
                <FileUpload
                  label="📷 Payment Screenshot"
                  accept="image/*"
                  value={formData.payment_screenshot_file}
                  onChange={(filename) => updateForm('payment_screenshot_file', filename)}
                />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between mb-2">
            {[1, 2, 3, 4, 5].map(num => (
              <div
                key={num}
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition ${
                  step >= num
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step > num ? <CheckCircle className="w-5 h-5" /> : num}
              </div>
            ))}
          </div>
          <div className="h-2 bg-gray-200 rounded-full">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all"
              style={{ width: `${(step / 5) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-3xl shadow-xl p-6 border-2 border-purple-100">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}

          {renderStep()}

          {/* Navigation Buttons */}
          <div className="flex gap-3 mt-6">
            {step > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition"
              >
                ← Back
              </button>
            )}
            {step === 1 && onBack && (
              <button
                type="button"
                onClick={onBack}
                className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition"
              >
                ← Cancel
              </button>
            )}
            {step < 5 ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold hover:opacity-90 transition flex items-center justify-center gap-2"
              >
                Next <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submitForm}
                disabled={loading}
                className="flex-1 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-xl font-bold hover:opacity-90 transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" /> Complete Registration
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;
